import { useEffect, useRef, useState } from 'react';

export interface LastSelection {
    key: string | null;
    index: number;
}

// The chosen key, else the last effective one, while it exists; otherwise the row now at its
// last index (or the new last row): the neighbour after a removal, the first row initially.
export function resolveSelectedKey(
    keys: readonly string[],
    chosenKey: string | null,
    last: LastSelection
): string | null {
    if (chosenKey !== null && keys.includes(chosenKey)) return chosenKey;
    if (last.key !== null && keys.includes(last.key)) return last.key;
    return keys[Math.min(last.index, keys.length - 1)] ?? null;
}

// Selection by VA id / client key, never by index; `initialKey` is selected first when given.
export function useVirtualAppointmentSelection(
    keys: string[],
    initialKey: string | null = null
): {
    selectedKey: string | null;
    select: (key: string) => void;
} {
    const [chosenKey, setChosenKey] = useState<string | null>(initialKey);
    // The effective selection as of the last render; removals above it shift its index.
    const lastRef = useRef<LastSelection>({ key: null, index: 0 });
    const selectedKey = resolveSelectedKey(keys, chosenKey, lastRef.current);
    const selectedIndex = selectedKey === null ? -1 : keys.indexOf(selectedKey);

    useEffect(() => {
        if (selectedIndex !== -1) lastRef.current = { key: selectedKey, index: selectedIndex };
    }, [selectedKey, selectedIndex]);

    return { selectedKey, select: setChosenKey };
}
